/**
 * Must match the template `name` shipped in `tasks` migration 0117 — the queue filter and the
 * workspace dispatcher both match it by string equality, and a mismatch fails silently: an empty
 * queue, a fall-through to `DefaultTaskWorkspace`, and nothing logged either way.
 *
 * Previously `statement_transaction_review`, which no migration has ever created — the backend
 * registers only `review_extracted_statement_transactions` (`hyke/tasks/constants.py`), seeded by
 * migration 0117 after it was renumbered from 0105. That migration `update_or_create`s a single
 * template, so there is no second one to disambiguate from.
 */
export enum TransactionExtractionTaskTemplateName {
    STATEMENT_TRANSACTION_REVIEW = 'review_extracted_statement_transactions',
}

export const TRANSACTION_EXTRACTION_TASK_TEMPLATE_NAMES = [
    TransactionExtractionTaskTemplateName.STATEMENT_TRANSACTION_REVIEW,
];

/** Sidebar sections keyed by task type, in the shape `TasksFilterSidebar` expects. */
export const TRANSACTION_EXTRACTION_SIDEBAR_FILTERS = {
    [TransactionExtractionTaskTemplateName.STATEMENT_TRANSACTION_REVIEW]: {
        label: 'Statement Transactions',
        taskTemplateNames: [TransactionExtractionTaskTemplateName.STATEMENT_TRANSACTION_REVIEW],
    },
};

/**
 * Leaf keys the grid leaves out. `section` is the statement heading a row sits under, inherited rather
 * than transcribed, so it has no citation of its own for a correction to be checked against.
 */
export const STATEMENT_REVIEW_HIDDEN_COLUMNS: readonly string[] = ['section'];

/**
 * Leaf keys in the order a statement prints them, so the grid reads left to right like the page beside
 * it. Matched without regard to case; a key not named here follows, in the order it first appeared.
 */
export const STATEMENT_REVIEW_COLUMN_ORDER: readonly string[] = [
    'transaction_date',
    'date',
    'description',
    'card_mask',
    'amount',
];

/** Leaf keys holding prose, which earn a full-width multiline box in the edit dialog. */
export const STATEMENT_REVIEW_LONG_TEXT_COLUMNS: readonly string[] = ['Description'];

/**
 * How a date is written back into a value.
 *
 * `MM/DD/YY` because that is what the extractor produces — every observed statement row reads
 * `05/22/26`. A picker writing `MM/DD/YYYY` would leave a manually added transaction formatted unlike
 * every row beside it, and whoever eventually parses this column would meet two shapes in one table.
 *
 * Named here rather than in the dialog so the format and the columns it applies to move together.
 */
export const STATEMENT_REVIEW_DATE_FORMAT = 'MM/DD/YY';

/**
 * What the reviewer concluded, carried in `completion_payload`. Accept is the only disposition by
 * design: a reject means the document should not have been up for review at all, which goes through
 * the header's Cancel task action instead and completes nothing.
 */
export enum StatementReviewDisposition {
    VERIFIED = 'verified',
}
